<template>
  <div class="product-card">
    <div class="product-card-header">
      <h3 class="product-card-header-name">{{ product.display_name }}</h3>
      <p class="mb-4">{{ product.description }}</p>
    </div>
    <div class="product-card-order">
      <div class="product-card-order-headings">
        <div class="column-1">{{ $t('productDetails.card.machines.serial') }}</div>
        <div v-if="isChroma(product.display_name)" class="column-2">
          {{ $t('productDetails.card.chroma.tier') }}
        </div>
        <div class="column-3">
          {{ $t('productDetails.card.machines.date') }}
        </div>
        <div v-if="isChroma(product.display_name)" class="column-4">
          {{ $t('productDetails.card.chroma.action') }}
        </div>
      </div>
      <div class="product-card-order-datas">
        <div v-if="!isChroma(product.display_name) || isChromaLegacy(product.display_name)">
          <div
            v-for="(order, index) in product.orders"
            :key="`detailed-${index}`"
            class="product-card-order-data"
          >
            <div class="column-1">
              {{ order.serial_number || '-' }}
            </div>
            <div v-if="isChroma(product.display_name)" class="column-2">
              {{ order.tier }}
            </div>
            <div class="column-3">{{ formatDate(order.created_at) }}</div>
            <div v-if="isChromaLegacy(product.display_name)" class="column-4">
              <a
                target="_blank"
                href="https://www.chromaembroidery.com/legacy-license"
                class="text-xs font-bold"
                style="color: #0083ea"
                v-if="isChromaLegacy(product.display_name) && false"
                >{{ $t('productDetails.card.chroma_legacy.upgrade') }}</a
              >
            </div>
          </div>
        </div>
        <div v-if="isChroma(product.display_name)" class="column-2">
          <div
            v-for="(licenses, index) in licensesKeys"
            :key="`detailed-${index}`"
            class="product-card-order-data"
          >
            <div class="column-1">
              {{ licenses.serialNumber }}
            </div>
            <div v-if="isChroma(product.display_name)" class="column-2">
              {{ licenses.tier }}
            </div>
            <div class="column-3">{{ formatDate(licenses.createdAt) }}</div>
            <div class="column-4">
              <a
                target="_blank"
                href="https://www.auth.ricoma.com/signin?redirect-url=https://www.chromaembroidery.com/account/plans?external=true&application=chroma"
                class="text-xs font-bold hidden"
                style="color: #0083ea"
              >
                {{ $t('productDetails.card.chroma_legacy.manage') }}
              </a>
            </div>
          </div>
          <div
            v-for="(pending, index) in pendingSubscriptions"
            :key="`detailed-${index}`"
            class="product-card-order-data"
          >
            <div class="column-1">
              {{ pending.perpetualSerialNumber || 'Pending activation' }}
            </div>
            <div v-if="isChroma(product.display_name)" class="column-2">
              {{ pending.tier }}
            </div>
            <div class="column-3">
              {{ pending.purchaseDate ? formatDate(pending.purchaseDate) : '' }}
            </div>
            <div class="column-4">
              <a
                target="_blank"
                :href="goToChroma(pending.uuid)"
                class="text-xs font-bold"
                style="color: #0083ea"
              >
                <span v-if="!pending.perpetualSerialNumber">{{
                  $t('productDetails.card.chroma_legacy.active')
                }}</span>
                <span v-if="pending.perpetualSerialNumber">{{
                  $t('productDetails.card.chroma_legacy.upgrade')
                }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isChroma(product.display_name) && !hasChromaLuxe" class="product-card-cta">
      <p class="product-card-cta-text w-full" v-if="!isChromaLegacy(product.display_name)">
        {{ $t('productDetails.card.chroma.note') }}
      </p>
      <div
        class="w-full"
        v-if="isChromaLegacy(product.display_name)"
        v-html="$t('productDetails.card.chroma_legacy.note')"
      ></div>
      <a
        v-if="!isChromaLegacy(product.display_name)"
        href="https://www.auth.ricoma.com/signin?redirect-url=https://www.chromaembroidery.com/account/plans?external=true&application=chroma"
        target="_blank"
        class="product-card-cta-link"
      >
        {{ $t('productDetails.card.chroma.link') }} →
      </a>
      <a
        v-if="isChromaLegacy(product.display_name)"
        href="https://www.chromaembroidery.com/whats-new"
        target="_blank"
        class="product-card-cta-link"
      >
        {{ $t('productDetails.card.chroma_legacy.link') }} →
      </a>
    </div>

    <div
      v-else-if="!isChroma(product.display_name) && hasPretrainigVideos"
      class="product-card-cta"
    >
      <p class="product-card-cta-text">
        {{ $t('productDetails.card.machines.text', { machine: product.display_name }) }}
      </p>
      <router-link
        tag="button"
        :to="{ name: 'training-lessons', params: { id: product.id } }"
        class="product-card-cta-link"
      >
        {{ $t('productDetails.card.machines.link') }} →
      </router-link>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { mapState } from 'vuex';
import { isChroma as checkForChroma } from '@/utils';
import { isChromaLegacy as checkForChromaLegacy } from '@/utils';

export default {
  name: 'ProductDetailsCard',
  props: {
    pendingSubscriptions: { type: Array, default: () => [] },
    licensesKeys: { type: Array, default: () => [] },
    product: { type: Object, default: () => ({}) },
  },
  tiers: {},
  computed: mapState({
    products: state => state.customer.products,
    hasPretrainigVideos() {
      return this.product &&
        this.product.display_name &&
        !this.isChroma(this.product.display_name) &&
        this.product.allows_pretraining
        ? this.product.has_videos
        : false;
    },
    onlyChromasName() {
      return this.product.orders.map(order => order.tier.toLowerCase());
    },
    hasChromaLuxe() {
      return this.product.orders.some(order => order.tier.toLowerCase().includes('luxe'));
    },
    chromaCTA() {
      const tier = Object.keys(this.$options.tiers).find(tier =>
        this.onlyChromasName.some(t => t.includes(tier)),
      );
      return this.$options.tiers[tier] ?? {};
    },
  }),
  methods: {
    goToChroma(uuid) {
      return `https://www.chromaembroidery.com/checkout?pid=${uuid}`;
    },
    formatDate(date) {
      if (date === '0000-00-00' || !date) return '-';
      const d = new Date(date);
      return format(d, 'P');
    },
    isChroma(name) {
      return checkForChroma(name);
    },
    isChromaLegacy(name) {
      return checkForChromaLegacy(name);
    },
  },
  async mounted() {
    await this.$store.dispatch('customer/getProducts');
  },
  created() {
    const tiers = {
      plus: {
        text: this.$t('productDetails.card.chroma.text', {
          chroma1: 'Chroma Plus',
          chroma2: 'Chroma Luxe',
        }),
        link:
          'https://shop.ricoma.com/collections/embroidery-software/products/chroma?variant=31414908452982',
      },
      inspire: {
        text: this.$t('productDetails.card.chroma.text', {
          chroma1: 'Chroma Inspire',
          chroma2: 'Chroma Plus',
        }),
        link:
          'https://shop.ricoma.com/collections/embroidery-software/products/chroma?variant=31414903275638',
      },
    };
    this.$options.tiers = tiers;
  },
};
</script>
